import classNames from 'classnames';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Segmented, StateMessage } from '@/components/ui/controls';
import { Badge, PageHeader } from '@/components/ui/page';
import { SortTh } from '@/components/ui/sort-th';
import { btn, table } from '@/components/ui/styles';
import { useIdentity } from '@/features/identity/identity';
import type { FollowUpWithLead } from '@/types/api';
import { formatDateTime, formatRelative, leadTitle, shownEmail } from '@/utils/format';
import http from '@/utils/http';
import { useApi } from '@/utils/use-api';
import { dateValue, useSort, type SortColumns } from '@/utils/use-sort';

type When = 'due' | 'upcoming' | 'done';
type Whose = 'everyone' | 'mine';

const bucket = (f: FollowUpWithLead, now: number): When => (f.done_at ? 'done' : new Date(f.due_at).getTime() <= now ? 'due' : 'upcoming');

const COLUMNS: SortColumns<FollowUpWithLead> = {
  at: { value: (f) => dateValue(f.done_at ?? f.due_at) },
  lead: { value: (f) => leadTitle(f.lead_name, f.lead_email) },
  by: { value: (f) => f.created_by },
};

const EMPTY: Record<When, string> = { due: 'Nothing due', upcoming: 'Nothing coming up', done: 'Nothing done yet' };

/**
 * Follow-ups (REM-1): every open follow-up the team has set, across leads, and the
 * ones done in the last 30 days (GET /follow-ups). The bot reminds the team chat
 * once when one is due. On a phone the table becomes a card per follow-up (UI-1, UI-2).
 */
const FollowUpsPage = () => {
  const { me } = useIdentity();
  const list = useApi<FollowUpWithLead[]>('/follow-ups');
  const items = list.data ?? [];
  const [when, setWhen] = useState<When>('due');
  const [whose, setWhose] = useState<Whose>('everyone');
  const [failed, setFailed] = useState(false);
  const now = Date.now();

  const mine = (f: FollowUpWithLead) => Boolean(me) && f.created_by_id === me?.id;
  const isShown = (f: FollowUpWithLead) => whose === 'everyone' || mine(f);
  const count = (w: When) => items.filter((f) => isShown(f) && bucket(f, now) === w).length;
  // The soonest first, or the latest done first, until a column header says otherwise.
  const { rows: shown, sort, toggle } = useSort(
    items
      .filter((f) => isShown(f) && bucket(f, now) === when)
      .sort((a, b) => (when === 'done' ? (b.done_at ?? '').localeCompare(a.done_at ?? '') : a.due_at.localeCompare(b.due_at))),
    COLUMNS,
  );
  const complete = (id: string) => {
    setFailed(false);
    http
      .post(`/follow-ups/${encodeURIComponent(id)}/done`)
      .then(list.reload)
      .catch(() => setFailed(true));
  };

  return (
    <div>
      <PageHeader title="Follow-ups" description="Every follow-up the team has set on a lead. When one is due, the bot reminds the team chat once." />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Segmented<When>
          label="When"
          value={when}
          onChange={setWhen}
          options={[
            { value: 'due', label: `Due ${count('due')}` },
            { value: 'upcoming', label: `Upcoming ${count('upcoming')}` },
            { value: 'done', label: `Done ${count('done')}` },
          ]}
        />
        <Segmented<Whose>
          label="Whose"
          value={whose}
          onChange={setWhose}
          options={[
            { value: 'everyone', label: 'Everyone' },
            { value: 'mine', label: 'Mine' },
          ]}
        />
      </div>

      {failed && <p className="mb-3 text-sm text-danger">That did not work. Try again.</p>}
      {list.error && !list.data ? (
        <StateMessage title="Follow-ups could not be loaded" tone="error">
          {list.error}
        </StateMessage>
      ) : !list.data ? (
        <div className="h-40 animate-pulse rounded-card bg-surface shadow-e1" />
      ) : shown.length === 0 ? (
        <StateMessage title={EMPTY[when]}>{whose === 'mine' ? 'Switch to Everyone to see the whole team’s.' : undefined}</StateMessage>
      ) : (
        <>
        <ul className="space-y-3 md:hidden">
          {shown.map((f) => {
            const at = when === 'done' && f.done_at ? f.done_at : f.due_at;
            return (
              <li key={f.id} className="rounded-card bg-surface p-4 shadow-e1">
                <div className="flex items-start justify-between gap-3">
                  <Link to={`/leads/${encodeURIComponent(f.lead_id)}`} className="-m-2 block min-w-0 rounded-lg p-2 transition-colors hover:bg-surface-hover">
                    <p className="truncate text-fg-strong">{leadTitle(f.lead_name, f.lead_email)}</p>
                    <p className="truncate text-xs text-fg-muted">{shownEmail(f.lead_email)}</p>
                  </Link>
                  {!f.done_at && (
                    <button type="button" className={btn.secondary} onClick={() => complete(f.id)}>
                      Done
                    </button>
                  )}
                </div>
                <p className="mt-2 text-sm text-fg-strong">
                  {when === 'done' ? 'Done ' : ''}
                  {formatDateTime(at)} <span className="text-xs text-fg-faint">· {formatRelative(at, now)}</span>
                </p>
                {f.note ? <p className="mt-1 break-words text-sm text-fg">{f.note}</p> : <p className="mt-1 text-sm text-fg-faint">No note</p>}
                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-fg-muted">
                  <span>Set by {mine(f) ? 'you' : f.created_by || '—'}</span>
                  {f.reminded_at ? <Badge tone="info">Sent to the team chat</Badge> : <span className="text-fg-faint">Reminder when due</span>}
                </div>
              </li>
            );
          })}
        </ul>
        <div className={classNames(table.wrap, 'hidden md:block')}>
          <div className="overflow-x-auto">
            <table className={classNames(table.table, 'min-w-[820px]')}>
              <thead className={table.head}>
                <tr>
                  <SortTh label={when === 'done' ? 'Done' : 'Due'} column="at" sort={sort} onSort={toggle} />
                  <SortTh label="Lead" column="lead" sort={sort} onSort={toggle} />
                  <th scope="col" className={table.th}>
                    Note
                  </th>
                  <SortTh label="Set by" column="by" sort={sort} onSort={toggle} />
                  <th scope="col" className={table.th}>
                    Reminder
                  </th>
                  <th scope="col" className={table.th}>
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {shown.map((f) => {
                  const at = when === 'done' && f.done_at ? f.done_at : f.due_at;
                  return (
                    <tr key={f.id} className={classNames(table.row, 'align-top')}>
                      <td className={classNames(table.td, 'whitespace-nowrap')}>
                        <p className="text-fg-strong">{formatDateTime(at)}</p>
                        <p className="text-xs text-fg-faint">{formatRelative(at, now)}</p>
                      </td>
                      <td className={table.td}>
                        <Link to={`/leads/${encodeURIComponent(f.lead_id)}`} className="text-fg-strong transition-colors hover:text-brand">
                          {leadTitle(f.lead_name, f.lead_email)}
                        </Link>
                        <p className="text-xs text-fg-muted">{shownEmail(f.lead_email)}</p>
                      </td>
                      <td className={classNames(table.td, 'max-w-sm text-fg')}>{f.note || <span className="text-fg-faint">No note</span>}</td>
                      <td className={classNames(table.td, 'whitespace-nowrap text-fg-muted')}>{mine(f) ? 'You' : f.created_by || '—'}</td>
                      <td className={table.td}>
                        {f.reminded_at ? <Badge tone="info">Sent to the team chat</Badge> : <span className="text-xs text-fg-faint">When due</span>}
                      </td>
                      <td className={classNames(table.td, 'text-right')}>
                        {!f.done_at && (
                          <button type="button" className={btn.quiet} onClick={() => complete(f.id)}>
                            Done
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        </>
      )}
    </div>
  );
};

export default FollowUpsPage;
